<template>
    <article class="article">
        <component :class="['blurCapImage', orientation]"
            :is="componentType"
            :to="routerLink">
            <div class="contentWrapper">
                <slot name="customMedia"
                    v-if="customMedia"/>
                <div :class="['coverWrapper', { sensitive: sensitiveOverlay }, { notSensitive: !sensitiveOverlay }]"
                    v-else-if="slider">
                    <img :class="['cover', 'sliderLeft', calculatePosition]"
                        :src="slider.left"
                        loading="lazy"
                        alt="Left side of slider image"/>
                    <img :class="['cover', 'sliderRight', calculatePosition]"
                        :src="slider.right"
                        loading="lazy"
                        alt="Right side of slider image"/>
                    <div class="divider"></div>
                    <div class="mediaIcon sliderIcon"></div>
                    <SensitiveOverlay
                        v-if="sensitiveOverlay"
                        @displayMedia="handleDisplay"/>
                </div>
                <div class="coverWrapper"
                    v-else>
                    <img :class="['cover', calculatePosition, { sensitive: sensitiveOverlay }, { sensitive: sensitiveOverlay }, { notSensitive: !sensitiveOverlay }]"
                        :src="image"
                        loading="lazy"
                        alt="Preview image of the post"/>
                    <div class="mediaIcon carouselIcon"
                        v-if="carouselLength"></div>
                    <SensitiveOverlay
                        v-if="sensitiveOverlay"
                        @displayMedia="handleDisplay"/>
                </div>
                <section class="content">
                    <img :class="['cover', 'blurredCover', calculatePosition]"
                        v-if="image"
                        :src="blurredImage"
                        loading="lazy"
                        alt="Background image for caption"/>
                    <div class="slot">
                        <slot/>
                    </div>
                </section>
                <Badge
                    v-if="badgeType"
                        :type="badgeType"
                        :icon="badgeIcon"
                        :label="badgeLabel"/>
            </div>
        </component>
    </article>
</template>

<script>
    import SensitiveOverlay from '@/components/entry/SensitiveOverlay'

    export default {
        name: 'BlurCapImage',
        components: {
            SensitiveOverlay
        },
        props: {
            orientation: {
                type: String,
                default: 'landscape',
                validator: type => ['landscape','portrait'].includes(type)
            },
            customMedia: {
                type: Boolean,
                default: false
            },
            image: {
                type: String
            },
            carousel: {
                type: Array
            },
            slider: {
                type: Object,
            },
            position: {
                type: Object
            },
            routerLink: {
                type: String
            },
            badgeType: {
                type: String,
                validator: type => ['recent','featured','related'].includes(type)
            },
            badgeLabel: {
                type: String
            },
            badgeIcon: {
                type: String
            },
            sensitive: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                showSensitive: false
            }
        },
        computed: {
            componentType() {
                if(this.routerLink) {
                    return 'router-link'
                } else {
                    return 'div'
                }
            },
            calculatePosition() {
                if(this.position && this.orientation === 'landscape') {
                    return this.position.landscape
                } else if(this.position && this.orientation === 'portrait') {
                    return this.position.portrait
                }
            },
            blurredImage() {
                if(this.image) {
                    return this.image
                } else {
                    return
                }
            },
            carouselLength() {
                if(this.carousel) {
                    return this.carousel.length > 1
                } else {
                    return
                }
            },
            sensitiveOverlay() {
                if(this.showSensitive) {
                    return false
                } else if(this.sensitive && !this.preferences.showSensitive) {
                    return true
                }
            }
        },
        methods: {
            handleDisplay() {
                this.showSensitive = true
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../../scss/variables';

    .article {
        display: flex;
        position: relative;
    }

    .blurCapImage {
        width: 100%;
        border-radius: 1rem;
        color: var(--color-white-important);
        transition: $transition-eio-20;
        position: relative;
        overflow: hidden;

        &:hover {
            transform: translateY(-0.5rem) scale(1.01);

            .contentWrapper {
                box-shadow: var(--shadow-xl);
            }
        }

        &:focus {
            outline: 0;
            box-shadow: var(--shadow-focus-s);
        }
    }

    .contentWrapper {
        display: flex;
        position: relative;
        height: 100%;
        border-radius: 1rem;
        box-shadow: var(--shadow-l);
        overflow: hidden;
        transition: $transition-eio-20;
        border-radius: 0.75rem;
    }

    .content {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        padding: 1.5rem;
        z-index: 1;
        overflow: hidden;
        background: var(--color-grey-100);
    }

    .landscape {
        .content {
            max-width: 24rem;
            height: auto;
        }

        .blurredCover {
            transform: scaleY(1.1) scaleX(-1.1);
        }
    }

    .portrait {
        .contentWrapper {
            flex-direction: column;
        }

        .blurredCover {
            transform: scaleY(-1.1) scaleX(1.1);
        }
    }

    .slot {
        position: relative;
        height: 100%;
    }

    .coverWrapper {
        position: relative;
        width: 100%;
        height: auto;
        left: 0;
        top: 0;
    }

    .cover {
        display: block;
        width: 100%;
        height: 28rem;
        min-height: 100%;
        background: var(--color-grey-200);
        object-fit: cover;
    }

    .divider {
        position: absolute;
        top: 0;
        left: 50%;
        width: 1px;
        height: 100%;
        background: var(--color-white-important);
    }

    .sliderRight {
        position: absolute;
        top: 0;
        left: 0;
        clip-path: polygon(50% 0, 100% 0, 100% 100%, 50% 100%);
    }

    .blurredCover {
        position: absolute;
        top: 0;
        left: 0;
        filter: blur(2rem) brightness(0.6);
    }

    .mediaIcon {
        position: absolute;
        top: 1rem;
        right: 1rem;
        width: 3.5rem;
        height: 3.5rem;
        padding: 0.5rem;
        user-select: none;
        display: block;
        border-radius: 0.75rem;
    }

    .carouselIcon {
        content: var(--carousel);
    }

    .sliderIcon {
        content: var(--slider);
    }

    .left {
        object-position: left;
    }

    .right {
        object-position: right;
    }

    .top {
        object-position: top;
    }

    .bottom {
        object-position: bottom;
    }

    .sensitive {
        filter: blur(4rem) brightness(0.8);
    }

    .notSensitive {
        filter: blur(0) brightness(1);
    }

    @media all and (max-width: 1200px) {
        .landscape {
            .content {
                max-width: 22rem;
            }
        }
    }

    @media all and (max-width: 560px) {
        .blurCapImage {
            min-height: 16rem;

            &:hover {
                transform: translateY(0) scale(1);
            }
        }

        .blurredCover {
            display: none;
        }

        .landscape {
            height: auto;

            .contentWrapper {
                flex-direction: column;
            }

            .content {
                max-width: 100%;
            }

            .blurredCover {
                object-position: bottom center;
                transform: scaleY(1.1) scaleY(-1.1);
            }
        }
    }
</style>